import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  Container,
  Typography,
  Box,
  Card,
  CardContent,
  CardActions,
  Grid,
  Chip,
  Button,
  Alert,
  Snackbar,
  CircularProgress,
  Avatar,
  Stack,
  Skeleton,
  Fade,
  Grow
} from '@mui/material';
import {
  Event as EventIcon,
  People as PeopleIcon,
  CalendarToday as CalendarIcon,
  AccessTime as TimeIcon,
  PersonAdd as JoinIcon,
  PersonRemove as LeaveIcon,
  Visibility as ViewIcon,
  Article as ArticleIcon,
  CheckCircle as JoinedIcon,
  Refresh as RefreshIcon,
  EmojiEvents as TrophyIcon
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { eventsService } from '../services/api';
import { 
  LoadingSpinner
} from '../components/Loading/LoadingComponents';
import { useAsyncOperation } from '../hooks/useLoading';

// Event Card Skeleton - matches the actual event card layout
const EventCardSkeleton = ({ animate = true }) => {
  return (
    <Fade in timeout={300}>
      <Card 
        sx={{ 
          height: '100%',
          display: 'flex',
          flexDirection: 'column',
          '&:hover': {
            transform: 'translateY(-2px)',
            transition: 'transform 0.2s ease-in-out'
          }
        }}
      >
        <CardContent sx={{ flexGrow: 1, p: 3 }}>
          {/* Title Row with Status Dot */}
          <Box sx={{ display: 'flex', alignItems: 'flex-start', mb: 1 }}>
            {/* Status Dot Skeleton */}
            <Skeleton 
              variant="circular" 
              width={8} 
              height={8} 
              sx={{ mr: 1.5, mt: 0.75, flexShrink: 0 }} 
            />
            
            {/* Title Skeleton */}
            <Box sx={{ flexGrow: 1, mr: 1 }}>
              <Skeleton 
                variant="text" 
                width="85%" 
                height={28} 
                sx={{ mb: 0.5 }}
              />
            </Box>
            
            {/* Status Chip Skeleton */}
            <Skeleton variant="rounded" width={60} height={20} />
          </Box>
          
          {/* Description skeleton */}
          <Box sx={{ ml: 2.5, mb: 2 }}>
            <Skeleton variant="text" width="100%" height={20} sx={{ mb: 0.5 }} />
            <Skeleton variant="text" width="90%" height={20} />
          </Box>
          
          {/* Date and Theme Row */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 3, ml: 2.5, mb: 1, flexWrap: 'wrap' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              <Skeleton variant="circular" width={16} height={16} />
              <Skeleton variant="text" width={120} height={20} />
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              <Skeleton variant="circular" width={16} height={16} />
              <Skeleton variant="text" width={80} height={20} />
            </Box>
          </Box>
          
          {/* Stats Row */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 3, ml: 2.5, flexWrap: 'wrap' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              <Skeleton variant="circular" width={16} height={16} />
              <Skeleton variant="text" width={100} height={20} />
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              <Skeleton variant="text" width={120} height={20} />
            </Box>
          </Box>
        </CardContent>
      </Card>
    </Fade>
  );
};

// Grid of event card skeletons - matches actual single-column layout
const EventsGridSkeleton = ({ count = 6 }) => {
  return (
    <Grid container spacing={2}>
      {Array.from({ length: count }).map((_, index) => (
        <Grid item xs={12} key={index}>
          <Grow in timeout={300 + (index * 100)}>
            <div>
              <EventCardSkeleton />
            </div>
          </Grow>
        </Grid>
      ))}
    </Grid>
  );
};

const Events = () => {
  const navigate = useNavigate();
  const { user, isAuthenticated } = useAuth();
  const [events, setEvents] = useState([]);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [actionLoading, setActionLoading] = useState({});
  const [showErrorSnackbar, setShowErrorSnackbar] = useState(false);
  const hasLoadedRef = useRef(false);

  // Enhanced loading
  const loading = useAsyncOperation('events');

  const loadEvents = useCallback(async () => {
    await loading.execute(
      async () => {
        // Use lightweight summary API for better performance
        const response = await eventsService.getEventsSummary({ 
          exclude_archived: true,
          limit: 100
        });
        console.log('Events summary API response:', response);
        // The API returns { data: { success: true, data: { events: [...] } } }
        setEvents(response.data.data.events || []);
        return response.data;
      },
      {
        phases: ['initial', 'processing'],
        onError: (err) => {
          setError('Failed to load events');
          console.error('Error loading events:', err);
          setShowErrorSnackbar(true);
        }
      }
    );
  }, []); // Empty dependency array to make function stable

  useEffect(() => {
    if (!hasLoadedRef.current) {
      hasLoadedRef.current = true;
      loadEvents();
    }
  }, [loadEvents]);

  // Enhanced sorting: Active events first, then Upcoming, then Closed
  const getSortedEvents = () => {
    if (!events.length) return [];

    // Filter to show upcoming, active, and closed events (exclude archived)
    const relevantEvents = events.filter(event => 
      event.status === 'active' || 
      event.status === 'upcoming' || 
      event.status === 'closed'
    );

    // Sort by priority: Active → Upcoming → Closed, with user participation as secondary factor
    return relevantEvents.sort((a, b) => {
      // Priority 1: Sort by status - Active, Upcoming, then Closed
      const statusPriority = { 
        'active': 1,    // Active events first (currently running)
        'upcoming': 2,  // Upcoming events second (not started yet)
        'closed': 3     // Closed events last (finished)
      };
      
      const aPriority = statusPriority[a.status] || 4;
      const bPriority = statusPriority[b.status] || 4;
      
      if (aPriority !== bPriority) {
        return aPriority - bPriority;
      }

      // Priority 2: Within same status, user's joined events come first
      if (isAuthenticated) {
        const aJoined = a.user_participating;
        const bJoined = b.user_participating;
        
        if (aJoined && !bJoined) return -1;
        if (!aJoined && bJoined) return 1;
      }

      // Priority 3: Sort by date within same status
      if (a.status === 'closed' && b.status === 'closed') {
        // For closed events, show most recently ended first
        return new Date(b.end_date) - new Date(a.end_date);
      } else if (a.status === 'active' && b.status === 'active') {
        // For active events, show those ending soonest first
        return new Date(a.end_date) - new Date(b.end_date);
      } else {
        // For upcoming events, show earliest start date first
        return new Date(a.start_date) - new Date(b.start_date);
      }
    });
  };

  const handleJoinEvent = async (eventId) => {
    if (!isAuthenticated) {
      setError('Please sign in to join events');
      return;
    }

    try {
      setActionLoading(prev => ({ ...prev, [eventId]: true }));
      await eventsService.joinEvent(eventId);
      setSuccess('Successfully joined the event!');
      loadEvents(); // Refresh to get updated participation status
    } catch (err) {
      setError('Failed to join event. Please try again.');
      console.error('Error joining event:', err);
    } finally {
      setActionLoading(prev => ({ ...prev, [eventId]: false }));
    }
  };

  const handleLeaveEvent = async (eventId) => {
    try {
      setActionLoading(prev => ({ ...prev, [eventId]: true }));
      await eventsService.leaveEvent(eventId);
      setSuccess('Successfully left the event');
      loadEvents(); // Refresh to get updated participation status
    } catch (err) {
      setError('Failed to leave event. Please try again.');
      console.error('Error leaving event:', err);
    } finally {
      setActionLoading(prev => ({ ...prev, [eventId]: false }));
    }
  };

  const handleViewEvent = (eventId) => {
    navigate(`/events/${eventId}`);
  };

  // Helper function to show timing info based on backend status
  const getTimeInfo = (event) => {
    const now = new Date();
    const start = new Date(event.start_date);
    const end = new Date(event.end_date);
    
    // Respect backend status - don't override with date calculations
    switch (event.status) {
      case 'upcoming':
        const diffToStart = start - now;
        const daysToStart = Math.ceil(diffToStart / (1000 * 60 * 60 * 24));
        if (daysToStart === 0) return 'starts today';
        if (daysToStart === 1) return 'starts tomorrow';
        return `starts in ${daysToStart} days`;
        
      case 'active':
        // For active events, show when they end (or that they're extended)
        const diffToEnd = end - now;
        const daysToEnd = Math.ceil(diffToEnd / (1000 * 60 * 60 * 24));
        if (daysToEnd > 0) {
          if (daysToEnd === 1) return 'ends tomorrow';
          return `${daysToEnd} days left`;
        } else {
          // Active but past end date - show it's been extended
          return 'active (extended)';
        }
        
      case 'closed':
      case 'archived':
        const diffSinceEnd = now - end;
        const daysSinceEnd = Math.floor(diffSinceEnd / (1000 * 60 * 60 * 24));
        if (daysSinceEnd === 0) return 'ended today';
        if (daysSinceEnd === 1) return 'ended yesterday';
        if (daysSinceEnd < 7) return `ended ${daysSinceEnd} days ago`;
        return `ended ${Math.floor(daysSinceEnd / 7)} weeks ago`;
        
      default:
        return '';
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'upcoming': return 'warning';
      case 'active': return 'success';
      case 'closed': return 'info';
      case 'cancelled': return 'error';
      default: return 'default';
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  const formatDateRange = (startDate, endDate) => {
    const start = new Date(startDate);
    const end = new Date(endDate);
    
    if (start.getFullYear() === end.getFullYear() && start.getMonth() === end.getMonth()) {
      return `${start.toLocaleDateString('en-US', { month: 'long', day: 'numeric' })} - ${end.getDate()}, ${end.getFullYear()}`;
    }
    
    return `${formatDate(startDate)} - ${formatDate(endDate)}`;
  };

  const sortedEvents = getSortedEvents();

  if (loading.isLoading) {
    return (
      <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
        {/* Header */}
        <Box sx={{ textAlign: 'center', mb: 4 }}>
          <Typography variant="h3" component="h1" gutterBottom>
            Writing Events
          </Typography>
          <Typography variant="h6" color="text.secondary">
            Join writing challenges and connect with other writers
          </Typography>
        </Box>
        
        <EventsGridSkeleton count={6} />
      </Container>
    );
  }

  return (
    <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
      {/* Header */}
      <Box sx={{ textAlign: 'center', mb: 4 }}>
        <Typography variant="h3" component="h1" gutterBottom>
          Writing Events
        </Typography>
        <Typography variant="h6" color="text.secondary" sx={{ mb: 3 }}>
          Join writing challenges and connect with other storytellers
        </Typography>
      </Box>

      {/* Events Grid */}
      {sortedEvents.length === 0 ? (
        <Card>
          <CardContent sx={{ textAlign: 'center', py: 6 }}>
            <EventIcon sx={{ fontSize: 64, color: 'text.secondary', mb: 2 }} />
            <Typography variant="h6" color="text.secondary" gutterBottom>
              No events available
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Check back soon for new writing events and challenges
            </Typography>
          </CardContent>
        </Card>
      ) : (
        <Grid container spacing={2}>
          {sortedEvents.map((event) => (
            <Grid item xs={12} key={event.event_id}>
              <Card 
                sx={{ 
                  height: '100%', 
                  display: 'flex', 
                  flexDirection: 'column',
                  '&:hover': {
                    boxShadow: 3,
                    cursor: 'pointer'
                  }
                }}
                onClick={() => handleViewEvent(event.event_id)}
              >
                <CardContent sx={{ flexGrow: 1, p: 3 }}>
                  {/* Title Row with Status Dot and Chips */}
                  <Box sx={{ display: 'flex', alignItems: 'flex-start', mb: 1 }}>
                    {/* Status Dot */}
                    <Box
                      sx={{
                        width: 8,
                        height: 8,
                        borderRadius: '50%',
                        backgroundColor: 
                          event.status === 'active' ? 'success.main' :
                          event.status === 'upcoming' ? 'warning.main' :
                          event.status === 'closed' ? 'info.main' :
                          'grey.400',
                        mr: 1.5,
                        mt: 0.75,
                        flexShrink: 0
                      }}
                    />
                    
                    {/* Event Title */}
                    <Typography 
                      variant="h6" 
                      component="h2" 
                      sx={{ 
                        flexGrow: 1, 
                        mr: 1,
                        fontSize: '1.1rem',
                        fontWeight: 600,
                        lineHeight: 1.3
                      }}
                    >
                      {event.title}
                    </Typography>
                    
                    {/* Status and Theme Chips */}
                    <Stack direction="row" spacing={0.5}>
                      {/* Status Chip */}
                      <Chip
                        label={event.status.charAt(0).toUpperCase() + event.status.slice(1)}
                        color={getStatusColor(event.status)}
                        size="small"
                        sx={{ fontSize: '0.75rem', fontWeight: 600, height: 24 }}
                      />
                      
                      {/* Joined Status Chip */}
                      {isAuthenticated && event.user_participating && (
                        <Chip
                          icon={<JoinedIcon />}
                          label="Joined"
                          color="success"
                          size="small"
                          variant="outlined"
                          sx={{ fontSize: '0.75rem', height: 24 }}
                        />
                      )}
                      
                      {/* Theme Chip */}
                      {event.theme && (
                        <Chip
                          label={event.theme}
                          color="secondary"
                          size="small"
                          variant="outlined"
                          sx={{ fontSize: '0.75rem', height: 24 }}
                        />
                      )}
                    </Stack>
                  </Box>

                  {/* Theme, Deadline, and Word Count */}
                  <Typography 
                    variant="body2" 
                    color="text.secondary" 
                    sx={{ mb: 2, ml: 2.5 }}
                  >
                    {event.theme && `${event.theme} • `}
                    {getTimeInfo(event)}
                    {(event.min_word_count || event.max_word_count) && (
                      ` • ${
                        event.min_word_count && event.max_word_count 
                          ? `${event.min_word_count}-${event.max_word_count} words`
                          : event.min_word_count 
                            ? `${event.min_word_count}+ words`
                            : `max ${event.max_word_count} words`
                      }`
                    )}
                  </Typography>

                  {/* Description - Creative Brief */}
                  {event.description && (
                    <Typography 
                      variant="body2" 
                      color="text.primary"
                      sx={{ 
                        mb: 2, 
                        ml: 2.5,
                        fontStyle: 'italic',
                        lineHeight: 1.4,
                        display: '-webkit-box',
                        WebkitLineClamp: 2,
                        WebkitBoxOrient: 'vertical',
                        overflow: 'hidden'
                      }}
                    >
                      "{event.description}"
                    </Typography>
                  )}

                  {/* Stats and Incentives Row */}
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 3, ml: 2.5, flexWrap: 'wrap' }}>
                    {/* Participants */}
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                      <PeopleIcon fontSize="small" sx={{ color: 'text.secondary' }} />
                      <Typography variant="body2" color="text.secondary">
                        {event.participant_count || 0} writers joined
                      </Typography>
                    </Box>
                    
                    {/* Submissions (for active/closed events) */}
                    {(event.status === 'active' || event.status === 'closed') && (
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                        <Typography variant="body2" color="text.secondary">
                          📝 {event.submission_count || 0} submissions
                        </Typography>
                      </Box>
                    )}

                    {/* Results Published Indicator */}
                    {event.status === 'closed' && event.results_published && (
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                        <TrophyIcon fontSize="small" sx={{ color: 'primary.main' }} />
                        <Typography variant="body2" color="primary.main" sx={{ fontWeight: 'medium' }}>
                          Results Available
                        </Typography>
                      </Box>
                    )}
                  </Box>

                  {/* View Results Button */}
                  {event.status === 'closed' && event.results_published && event.status !== 'archived' && (
                    <Box sx={{ mt: 2, ml: 2.5 }}>
                      <Button
                        variant="contained"
                        size="small"
                        startIcon={<TrophyIcon />}
                        onClick={(e) => {
                          e.stopPropagation();
                          navigate(`/events/${event.event_id}/results`);
                        }}
                        sx={{ 
                          background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                          '&:hover': {
                            background: 'linear-gradient(135deg, #5a6fd8 0%, #6a4190 100%)',
                          }
                        }}
                      >
                        View Results
                      </Button>
                    </Box>
                  )}
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>
      )}

      {/* Success/Error Snackbars */}
      <Snackbar
        open={!!success}
        autoHideDuration={6000}
        onClose={() => setSuccess('')}
      >
        <Alert onClose={() => setSuccess('')} severity="success">
          {success}
        </Alert>
      </Snackbar>

      <Snackbar
        open={!!error}
        autoHideDuration={6000}
        onClose={() => setError('')}
      >
        <Alert onClose={() => setError('')} severity="error">
          {error}
        </Alert>
      </Snackbar>

      {/* Enhanced Error Snackbar */}
      <Snackbar
        open={showErrorSnackbar}
        autoHideDuration={6000}
        onClose={() => setShowErrorSnackbar(false)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert 
          onClose={() => setShowErrorSnackbar(false)} 
          severity="error"
          action={
            <Button color="inherit" size="small" onClick={loadEvents}>
              <RefreshIcon sx={{ mr: 0.5 }} />
              Retry
            </Button>
          }
        >
          Failed to load events. Please try again.
        </Alert>
      </Snackbar>
    </Container>
  );
};

export default Events;
